import { FlexCol, FlexRow, GraphLegends } from '@/src/components'
import { BaseText } from '@/src/components/BaseText'
import { GraphTick2 } from '@/src/components/GraphTick2'
import { ResetZoomButton } from '@/src/components/charts/ResetZoomButton'
import { useChartZoom } from '@/src/components/charts/useChartZoom.hook'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { useXAxisProps } from '@/src/features/analytics/useXAxisProps.hook'
import type { LoadPlanningAdjustmentFormValidated } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.types'
import { useLoadPlanningDataCurvePreview } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/hooks/useLoadPlanningPreview.hook'
import {
  getPeakPointValue,
  getPreviewColor,
  getPreviewDataByGranularity,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/preview-graph/LoadPlanningPreview.utils'
import { LoadPlannningPreviewGraphTooltip } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/preview-graph/LoadPlanningPreviewGraphTooltip'
import {
  getDateFormatByGranularity,
  getEnergyUnitType,
  mapLoadPlanningUnitText,
  zoomDataPointsTimeRange,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { appColors } from '@/src/theme'
import { daysBetweenDates } from '@/src/utils/dateUtil'
import { UNIT_SCALE_RANGE, scaleUnitToTargetValue } from '@/src/utils/measurementUnitUtils'
import { Box, Flex, Spinner } from '@chakra-ui/react'
import { formatNumberCompact } from '@shared/utils'
import { format } from 'date-fns'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from 'recharts'
import { getNiceTickValues } from 'recharts-scale'

interface LoadPlanningPreviewGraphProps {
  dataToPreview?: LoadPlanningAdjustmentFormValidated
  granularity: GranularityEnum
  dateRange?: { from: Date; to: Date }
}

export const LoadPlanningPreviewGraph = ({ dataToPreview, granularity, dateRange }: LoadPlanningPreviewGraphProps) => {
  const { t } = useTranslation()

  const { data: loadForecastData, isFetching: isForecastLoading } = useLoadPlanningDataCurvePreview(dataToPreview, granularity)
  const fullDataSource = loadForecastData ? getPreviewDataByGranularity(loadForecastData, granularity) : []
  const dataSource = zoomDataPointsTimeRange(fullDataSource, dateRange)

  const dataWithTimestamp = useMemo(() => dataSource.map(d => ({ ...d, timestamp: d.timestampStart })), [dataSource])
  const { zoomedData, isZoomed, selectionStart, selectionEnd, isSelecting, handleMouseDown, handleMouseMove, handleMouseUp, resetZoom } =
    useChartZoom({ data: dataWithTimestamp })

  const chartMouseHandlers = {
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseUp,
  }

  const isDateRangeMoreThanTwoDays = dateRange ? daysBetweenDates(dateRange.from, dateRange.to) > 2 : false

  const {
    ref: graphContainerRef,
    tickFormatProps,
    xAxisInterval,
  } = useXAxisProps({
    dataPoints: zoomedData ?? [],
    datePropertyName: 'timestampStart',
    granularity: isDateRangeMoreThanTwoDays ? GranularityEnum.ONE_DAY : granularity,
  })

  if (!loadForecastData) {
    return (
      <FlexCol w={'100%'} h={'100%'} justifyContent={'center'} alignItems={'center'}>
        <Spinner />
        <Box>{t('components.procurement.forecasting.modal.form.calculating_results')}</Box>
      </FlexCol>
    )
  }

  const maxValue = getPeakPointValue(zoomedData, granularity)
  const niceTickValues = getNiceTickValues([0, maxValue], 5, false)
  const legends = [
    {
      text: t('components.procurement.forecasting.graph.preview_adjusted_load'),
      lineColor: getPreviewColor('draftValue'),
      iconType: 'box' as const,
    },
    {
      text: t('components.procurement.forecasting.graph.preview_preview_load'),
      lineColor: getPreviewColor('previewValue'),
      iconType: 'box' as const,
    },
  ]

  const unitProperty = getEnergyUnitType(granularity)
  const unit = mapLoadPlanningUnitText(zoomedData[0]?.draftValue?.[unitProperty]?.unit) || 'kWh'
  const { targetUnit, scaler } = scaleUnitToTargetValue(maxValue, unit, UNIT_SCALE_RANGE)

  return (
    <Box position='relative'>
      <FlexRow justifyContent={'space-between'} width={'100%'}>
        <Flex>
          <BaseText color='gray.400' fontSize={14} marginBottom={2}>
            {targetUnit}
          </BaseText>
        </Flex>
        <Flex gap={4} alignItems='center' h={'12px'}>
          <ResetZoomButton onReset={resetZoom} isVisible={isZoomed} />
          <GraphLegends legends={legends} />
        </Flex>
      </FlexRow>
      <ResponsiveContainer width={'100%'} height={420} ref={graphContainerRef}>
        <ComposedChart data={zoomedData} {...chartMouseHandlers} style={{ cursor: 'crosshair' }}>
          <XAxis
            dataKey={'timestampStart'}
            tickLine={false}
            tick={props => (
              <GraphTick2
                tickProps={props}
                formatProps={{ ...tickFormatProps, showYear: granularity !== GranularityEnum.ONE_MONTH }}
                customValueFormatter={v => format(new Date(v), getDateFormatByGranularity(granularity))}
              />
            )}
            tickMargin={8}
            axisLine={{ stroke: appColors.gray[100] }}
            interval={granularity === GranularityEnum.ONE_MONTH ? 0 : xAxisInterval}
          />
          <YAxis
            type='number'
            domain={[0, niceTickValues.at(-1) || 0]}
            ticks={niceTickValues}
            tickLine={false}
            axisLine={false}
            tickMargin={0}
            tick={{
              fill: appColors.gray['400'],
            }}
            tickFormatter={value => formatNumberCompact(value * scaler, { maximumFractionDigits: 1 })}
            label={{
              value: unit,
              position: 'top',
              style: { fill: appColors.gray['600'] },
            }}
          />
          <CartesianGrid vertical={false} stroke={'#F3F3F3'} />
          {getChartComponents(granularity)}
          <Tooltip
            cursor={!isSelecting}
            content={(props: TooltipProps<number, string>) => <LoadPlannningPreviewGraphTooltip {...props} granularity={granularity} />}
            wrapperStyle={{ outline: 'none' }}
          />
          {isSelecting && selectionStart && selectionEnd && (
            <ReferenceArea
              x1={selectionStart}
              x2={selectionEnd}
              fill={appColors.blue['500']}
              fillOpacity={0.2}
              stroke={appColors.blue['500']}
              strokeOpacity={0.5}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      {isForecastLoading && (
        <Flex position='absolute' inset={0} bg='whiteAlpha.700' align='center' justify='center'>
          <Spinner />
        </Flex>
      )}
    </Box>
  )
}

const getChartComponents = (granularity: GranularityEnum) => {
  const unitProperty = getEnergyUnitType(granularity)
  if (granularity === GranularityEnum.FIFTEEN_MINUTES || granularity === GranularityEnum.ONE_HOUR) {
    return (
      <>
        <Line
          dataKey={`draftValue.${unitProperty}.amount`}
          type={'monotone'}
          strokeWidth={1}
          dot={false}
          stroke={getPreviewColor('draftValue')}
          isAnimationActive={false}
        />
        <Line
          dataKey={`previewValue.${unitProperty}.amount`}
          type={'monotone'}
          strokeWidth={1}
          dot={false}
          stroke={getPreviewColor('previewValue')}
          isAnimationActive={false}
        />
      </>
    )
  }
  return (
    <>
      <Bar
        radius={[4, 4, 0, 0]}
        barSize={11}
        dataKey={`draftValue.${unitProperty}.amount`}
        shape={props => <Rectangle {...props} fill={getPreviewColor('draftValue')} />}
        isAnimationActive={false}
      />
      <Bar
        radius={[4, 4, 0, 0]}
        barSize={11}
        dataKey={`previewValue.${unitProperty}.amount`}
        shape={props => <Rectangle {...props} fill={getPreviewColor('previewValue')} />}
        isAnimationActive={false}
      />
    </>
  )
}
